import { useCallback, useEffect, useState } from "react";
import "./index.css";
import { ProjectsList } from "./ProjectsList";
import { refetchState } from "./state";
import { Infobanner, InfobannerType } from "./Infobanner";
import { useTabs } from "./useTabs";
import { TimePeriodView } from "./TimePeriodView";
import { useProjectsSortedByTime } from "./useProjectsSortedByTime";
import { useEventsGroupedByPeriod } from "./useEventsGroupsByPeriod";

const SCOPES = "https://www.googleapis.com/auth/calendar.readonly";
export const UNNAMED_PROJECT = "untitled project";
export const CLIENT_PROJECT_SEPARATOR = "{_SEP_}";

function getISOWeekNumber(date: Date) {
  // Copy UTC date
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );

  // ISO weekday (1 = Monday, 7 = Sunday)
  const dayNum = d.getUTCDay() || 7;

  // Move to nearest Thursday (ISO 8601 rule)
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);

  // ISO year is the year of this Thursday
  const isoYear = d.getUTCFullYear();

  // Start of ISO year
  const yearStart = new Date(Date.UTC(isoYear, 0, 1));

  // Calculate ISO week number
  const week = Math.ceil(((+d - +yearStart) / 86400000 + 1) / 7);

  return { year: isoYear, week };
}

interface CalendarListEntry {
  id: string;
  summary: string;
}

interface EventDateTime {
  date?: string;
  dateTime?: string;
}

export interface CalendarEvent {
  id: string;
  summary?: string;
  start?: EventDateTime;
  end?: EventDateTime;
}

export interface Project {
  time: number;
  latestEventTime: number;
  done: boolean;
  events: CalendarEvent[];
}

export interface ProjectsSortedByTime {
  keys: string[];
  projects: {
    [name: string]: undefined | Project;
  };
}

export interface CalendarEventsByTimePeriod {
  periodName: string;
  time: number;
  events: CalendarEvent[];
}

export function App() {
  const [accessToken, setAccessToken] = useState(
    localStorage.getItem("google_access_token"),
  );
  const [errorStack, setErrorStack] = useState<Error[]>([]);
  const [calendars, setCalendars] = useState<CalendarListEntry[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [activeCalendarID, setActiveCalendarID] = useState(
    localStorage.getItem("active_calendar_id"),
  );
  const needsFetch = refetchState((x) => x.needsFetch);
  const setLastFetch = refetchState((x) => x.setLastFetch);
  const lastFetch = refetchState((x) => x.lastFetch);
  const isFetching = refetchState((x) => x.isFetching);
  const incrIsFetching = refetchState((x) => x.incrIsFetching);
  const decrIsFetching = refetchState((x) => x.decrIsFetching);

  const addError = useCallback(
    (e: Error) => {
      console.log("Adding error:");
      console.error(e);
      setErrorStack((x) => [...x, e]);
    },
    [setErrorStack],
  );

  const clearError = useCallback(
    (message: string) => {
      setErrorStack((x) => x.filter((x) => x.message !== message));
    },
    [setErrorStack, errorStack],
  );

  const clearAllErrors = useCallback(() => {
    setErrorStack([]);
  }, [setErrorStack]);

  useEffect(() => {
    // Ensure the Google client script is loaded
    if (!window.google) return;
  }, []);

  const handleAuth = () => {
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id:
        "247773297623-cr40vc3m8akuscbjbfavgc8hila2nfnd.apps.googleusercontent.com",
      scope: SCOPES,
      callback: async (tokenResponse: { access_token?: string }) => {
        try {
          if (tokenResponse.access_token) {
            setAccessToken(tokenResponse.access_token);
          } else {
            throw new Error("Got no access token back!");
          }
        } catch (e) {
          if (e instanceof Error) {
            addError(e);
          }
        }
      },
    });

    client.requestAccessToken();
  };

  // Store access token in cookie
  useEffect(() => {
    if (accessToken) {
      localStorage.setItem("google_access_token", accessToken);
    }
  }, [accessToken]);

  const fetchCalendars = useCallback(
    async (token: string) => {
      incrIsFetching();
      try {
        const res = await fetch(
          "https://www.googleapis.com/calendar/v3/users/me/calendarList",
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const data = await res.json();
        if (data.error && data.error.code && data.error.message) {
          if (data.error.code === 401) {
            logout();
          } else {
            throw new Error(data.error.message);
          }
        }
        setCalendars(data.items || []);
      } catch (e) {
        if (e instanceof Error) {
          addError(e);
        }
      } finally {
        decrIsFetching();
      }
    },
    [setCalendars, addError, decrIsFetching, incrIsFetching],
  );

  const loadCalendarEvents = useCallback(
    async (accessToken: string, calendarId: string) => {
      incrIsFetching();
      try {
        // Fetch Calendar events
        const res = await fetch(
          `https://www.googleapis.com/calendar/v3/calendars/${calendarId}/events?maxResults=5000&orderBy=startTime&singleEvents=true&timeMin=` +
            new Date("2024-01-01").toISOString(),
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );

        const data = await res.json();
        if (data.error) {
          if (data.error.code === 401) {
            return logout();
          }
          throw new Error(data.error.message);
        }
        setEvents(data.items || []);
        clearAllErrors();
        setLastFetch(Date.now());
      } catch (e) {
        if (e instanceof Error) {
          addError(e);
        }
      } finally {
        decrIsFetching();
      }
    },
    [
      setErrorStack,
      clearAllErrors,
      decrIsFetching,
      incrIsFetching,
      setLastFetch,
    ],
  );

  // Load calendars
  useEffect(() => {
    if (accessToken) {
      fetchCalendars(accessToken);
    }
  }, [accessToken]);

  // Load events
  useEffect(() => {
    if (accessToken && activeCalendarID) {
      if (needsFetch > lastFetch) {
        loadCalendarEvents(accessToken, activeCalendarID);
      }
    }
  }, [accessToken, activeCalendarID, lastFetch, needsFetch]);

  const projectsSortedByTime = useProjectsSortedByTime(events, addError);

  const logout = useCallback(() => {
    setCalendars([]);
    setEvents([]);
    setAccessToken(null);
    localStorage.removeItem("google_access_token");
    localStorage.removeItem("active_calendar_id");
    setErrorStack([]);
  }, [setEvents, setCalendars, setAccessToken]);

  const { tabs, activeTab, setTab } = useTabs(
    ["Projects", "Per month", "Per week"],
    "Projects",
  );

  const eventsByMonth = useEventsGroupedByPeriod(events, (d) => {
    let monthAndYear = new Intl.DateTimeFormat("sv-SE", {
      timeZone: "Europe/Stockholm",
      month: "long",
      year: "numeric",
    }).format(d);
    monthAndYear = monthAndYear[0].toUpperCase() + monthAndYear.substring(1);
    return monthAndYear;
  });

  const eventsByWeek = useEventsGroupedByPeriod(events, (d) => {
    const { week, year } = getISOWeekNumber(d);
    return `Week ${week}, ${year}`;
  });

  return (
    <div className="p-10">
      <div className=" fixed right-10 top-10 flex flex-col gap-1 text-sm">
        {accessToken ? (
          <button
            className="bg-blue-500 text-white px-3 py-1 rounded"
            onClick={logout}
          >
            Logout
          </button>
        ) : (
          <button
            className="bg-blue-500 text-white px-3 py-1 rounded"
            onClick={handleAuth}
          >
            Login
          </button>
        )}
        {activeCalendarID && accessToken && (
          <button
            className="bg-blue-500 text-white px-3 py-1 rounded"
            onClick={() => loadCalendarEvents(accessToken, activeCalendarID)}
          >
            Reload
          </button>
        )}
      </div>
      {!accessToken && <div>Login to start!</div>}
      <div className=" flex items-start flex-col gap-2">
        {errorStack.map((e) => {
          return (
            <Infobanner type={InfobannerType.Error} key={e.message}>
              {e.message}{" "}
              <button
                className="bg-gray-100 p-2 ml-20 text-black border-gray-300 border rounded cursor-pointer"
                onClick={() => clearError(e.message)}
              >
                Hide
              </button>
            </Infobanner>
          );
        })}
      </div>
      {isFetching > 0 && (
        <div className="fixed left-10 top-3 z-10">
          <Infobanner type={InfobannerType.Info}>Loading...</Infobanner>
        </div>
      )}
      {accessToken && (
        <>
          {!activeCalendarID && calendars.length > 0 && (
            <div className="space-y-2">
              <h2 className="text-lg font-semibold">Select calendar</h2>
              <div className="flex flex-wrap gap-2 flex-col items-start">
                {calendars.map((cal) => (
                  <button
                    key={cal.id}
                    className="bg-gray-200 px-3 py-1 rounded hover:bg-gray-300 cursor-pointer"
                    onClick={() => {
                      localStorage.setItem("active_calendar_id", cal.id);
                      setActiveCalendarID(cal.id);
                    }}
                  >
                    {cal.summary}
                  </button>
                ))}
              </div>
            </div>
          )}
          {activeCalendarID && (
            <>
              <div className=" flex gap-4 flex-wrap">
                {tabs.map((tab) => {
                  const active = tab === activeTab;
                  return (
                    <a
                      key={tab}
                      onClick={() => setTab(tab)}
                      className={`text-lg cursor-pointer ${active ? "text-black" : "text-gray-400"} font-semibold pb-2 transition-colors`}
                    >
                      {tab}
                    </a>
                  );
                })}
              </div>
              {activeTab === "Projects" && (
                <ProjectsList projectsSortedByTime={projectsSortedByTime} />
              )}
              {activeTab === "Per month" && (
                <TimePeriodView groupedEvents={eventsByMonth} />
              )}
              {activeTab === "Per week" && (
                <TimePeriodView groupedEvents={eventsByWeek} />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
